import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CreditCardDisplay from './CreditCardDisplay';
import { TrendingUp, Gift, Wallet, RefreshCw, CreditCard, Users, Eye, Plus, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getCardStatusBadge, isCardClosed } from '../utils/cardStatus';
import { SESSION_KEY } from '../helper/common';
import api from '../api';
import OTPVerificationModal from './OTPVerificationModal';
import { set } from 'react-hook-form';


const config = {
  labels: {
    title: 'Home',
    subtitle: 'Monitor your credit card activity, spending limits, and rewards in one place.',
    creditUtilization: 'Credit Utilization',
    availableLimit: 'Available Limit',
    consumedLimit: 'Consumed Limit',
    rewardPoints: 'Reward Points',
    pointsValue: 'Points Value',
    userDetails: 'User Details',
    email: 'Email',
    mobile: 'Mobile',
    loading: 'Loading data...',
    error: 'Failed to fetch data from service',
    retry: 'Update Home',
    manageCard: 'Manage Card Settings',
    cardList: 'Your Cards',
    cardholderName: 'Cardholder Name',
    creditCardNumber: 'Credit Card Number',
    status: 'Status',
    cardType: 'Card Type',
    actions: 'Actions',
    cardLimit: 'Card Limit',
    primaryCard: 'Basic Card',
    supplementaryCard: 'Supplementary Card',
    viewDetails: 'View Details',
    manage: 'Manage',
    requestLimitIncrease: 'Set Limit',
    monthlyExceedAmount: 'Enter Required Limit for Supplementary Card',
    submit: 'Submit Request',
    cancel: 'Cancel',
    limitRequestSuccess: 'Set limit request submitted successfully!',
    limitRequestError: 'Failed to submit limit increase request.'
  },
  apiUrl: process.env.REACT_APP_API_URL_User,
  currency: 'Rs',
};

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [cardList, setCardList] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedCardForLimit, setSelectedCardForLimit] = useState(null);
  const [exceedAmount, setExceedAmount] = useState(0);
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [downloadingLetter, setDownloadingLetter] = useState(false);

  const [showOTPModal, setShowOTPModal] = useState(false);


  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const cnic = localStorage.getItem('cnic');
      if (!cnic) {
        throw new Error('CNIC not found. Please login again.');
      }
      const token = localStorage.getItem(SESSION_KEY);
      // Fetch main dashboard data
      // const encryptData = getEncryptedHeaderObject(cnic,custId,cardNumber);
      const response = await axios.get(`${config.apiUrl}/api/user/dashboard`, {
        params: { cnic },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const rawData = response.data;
      // console.log('Raw service response:', rawData);
      const mappedData = {
        holderName: rawData.cardHolderName || 'N/A',
        cardNumber: rawData.cardNo || 'N/A',
        lastFourDigits: rawData.cardNo ? rawData.cardNo.slice(-4) : 'N/A',
        cardNoFirstSixDigits: rawData.cardNo ? rawData.cardNo.substring(0, 6) : 'N/A',
        expiryDate: rawData.expiryDate || 'N/A',
        totalLimit: rawData.creditLimit || 0,
        availableLimit: rawData.availableLimit || 0,
        consumedLimit: rawData.creditLimit ? (rawData.creditLimit - (rawData.availableLimit || 0)) : 0,
        points: rawData.points || 0,
        pointsValue: rawData.pointsValue || 0,
        email: rawData.email || 'N/A',
        mobileNo: rawData.mobileNo || 'N/A',
        cardStatus: rawData.cardStatus || 'N/A',
        custId: rawData.customerId || 'N/A',
        currency: rawData.currency || config.currency,
      };
      setUserData(mappedData);

      // Store primary card data
      if (mappedData.cardNumber && mappedData.cardNumber !== 'N/A') {
        localStorage.setItem('cardNumber', mappedData.cardNumber);
        localStorage.setItem('cardStatus', mappedData.cardStatus);
        localStorage.setItem('custId', mappedData.custId);
        localStorage.setItem('totalLimit', mappedData.totalLimit.toString());
        localStorage.setItem('availableLimit', mappedData.availableLimit.toString());
        localStorage.setItem(SESSION_KEY, rawData.token);
      }
      // Fetch card list
      await fetchCardList(mappedData.custId, rawData.token);

      if (localStorage.getItem('showDashboardToast') === 'true') {
        toast.success('Dashboard data loaded successfully!');
        localStorage.removeItem('showDashboardToast');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      const errorMsg = err.response?.data?.message || err.message || config.labels.error;
      setError(errorMsg);
      toast.error(errorMsg);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardList = async (custId, token) => {
    try {

      const response = await axios.get(`${config.apiUrl}/api/user/list`, {
        params: { custId },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const cardsData = response.data.cards || [];

      setCardList(cardsData);
      if (cardsData.length > 0) {
        setSelectedCard(cardsData[0]);
      }

      // Share blocked-card state with Header without an extra API call
      const hasTempBlocked = cardsData.some(c => c.status === 'T');
      sessionStorage.setItem('hasBlockedCards', hasTempBlocked ? 'true' : 'false');
      window.dispatchEvent(new CustomEvent('cardsUpdated', { detail: { hasBlockedCards: hasTempBlocked } }));
    } catch (err) {
      console.error('Error fetching card list:', err);
      toast.error('Failed to load card list');
    }
  };






  const handleDownloadLetter = async (card) => {
    try {
      setDownloadingLetter(true);
      const cnic = localStorage.getItem('cnic');
      const customerId = localStorage.getItem('custId');
      const token = localStorage.getItem('authToken');

      if (!cnic || !customerId) {
        toast.error('CNIC or Customer ID not found. Please login again.');
        return;
      }

      const response = await axios.get(
        `${config.apiUrl}/api/cards/closure-letter`,
        {
          params: { cnic, customerId },
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob',   // important — PDF binary
        }
      );

      // Create a temporary URL and trigger browser download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ClosureLetter_${customerId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Closure letter downloaded successfully.');
    } catch (err) {
      console.error('Error downloading closure letter:', err);
      toast.error('Failed to download closure letter. Please try again.');
    } finally {
      setDownloadingLetter(false);
    }
  };

  const handleManageCard = (card = null) => {
    const cardToManage = card || selectedCard;
    if (cardToManage && cardToManage.cardNumber) {
      localStorage.setItem('cardNumber', cardToManage.cardNumber);
      localStorage.setItem('cardStatus', cardToManage.status);
      navigate('/CardManage', {
        state: {
          cardNumber: cardToManage.cardNumber,
          cardStatus: cardToManage.status,
          cardType: cardToManage.cardType
        }
      });
    } else {
      toast.error('Card number not available');
    }
  };

  const handleManageCashBack = (card = null) => {
    const cardToCashBack = card || selectedCard;
    if (cardToCashBack && cardToCashBack.cardNumber) {
      localStorage.setItem('cardNumber', cardToCashBack.cardNumber);

      navigate('/CashBack', {
        state: {
          cardNumber: cardToCashBack.cardNumber,

        }
      });
    } else {
      toast.error('Card number not available');
    }
  };

  const handleViewStatements = () => {
    if (userData && userData.custId && userData.custId !== 'N/A') {
      localStorage.setItem('custId', userData.custId);
      localStorage.setItem('totalLimit', userData.totalLimit);
      localStorage.setItem('availableLimit', userData.availableLimit);
      navigate('/statements');
    } else {
      toast.error('Card number not available');
    }
  };

  const handleViewTaxCertificate = () => {
    if (userData && userData.custId && userData.custId !== 'N/A') {
      localStorage.setItem('custId', userData.custId);
      localStorage.setItem('cardNumber', userData.cardNumber);
      navigate('/tax-certificate');
    } else {
      toast.error('Card number not available');
    }
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
  };

  const handleRequestLimitIncrease = (card) => {
    setSelectedCardForLimit(card);
    setExceedAmount(0);
    setAgreedToTerms(false);
    setShowLimitModal(true);
  };



  const handleSubmitLimitRequest = async () => {
    setAgreedToTerms(false);
    if (!selectedCardForLimit || exceedAmount <= 0) {
      toast.error('Please enter a valid amount greater than 0.');
      return;
    }

    try {
      const token = localStorage.getItem(SESSION_KEY);

      await axios.post(`${config.apiUrl}/api/user/request-monthly-limit-exceed`, {
        cardNumber: selectedCardForLimit.cardNumber,
        amount: exceedAmount
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchUserData();
      toast.success(config.labels.limitRequestSuccess);
      setShowLimitModal(false);
      setSelectedCardForLimit(null);
      setExceedAmount(0);
    } catch (err) {
      console.error('Error submitting limit request:', err);
      const errorMsg = err.response?.data?.message || config.labels.limitRequestError;
      toast.error(errorMsg);
    }
  };

  const getCardTypeBadge = (cardType, isPrimary) => {
    if (isPrimary) return { label: config.labels.primaryCard, color: 'blue' };
    return { label: config.labels.supplementaryCard, color: 'purple' };
  };

  const financialData = userData;
  // console.log('Financial Data:', financialData);
  const utilizationPercentage = financialData && financialData.totalLimit ?
    ((financialData.consumedLimit / financialData.totalLimit) * 100).toFixed(1) : 0;

  const utilizationBarWidth = Math.min(Math.max(Number(utilizationPercentage) || 0, 0), 100);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">{config.labels.loading}</div>
      </div>
    );
  }

  if (error || !financialData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={fetchUserData}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <RefreshCw className="h-4 w-4" />
            <span>{config.labels.retry}</span>
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'green', customIcon, delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      transition={{ duration: 0.3, delay }}
      className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50 hover:shadow-xl hover:bg-white/90 hover:scale-105 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${color}-100 dark:bg-${color}-900/50 shadow-sm`}>
          {customIcon ? (
            <span className={`text-2xl font-bold text-${color}-700 dark:text-${color}-400`}>{customIcon}</span>
          ) : (
            <Icon className={`h-6 w-6 text-${color}-700 dark:text-${color}-400`} />
          )}
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-500">{subtitle}</p>}
      </div>
    </motion.div>
  );

  const cardData = {
    cardNoFirstSixDigits: financialData.cardNoFirstSixDigits,
    holderName: financialData.holderName,
    lastFourDigits: financialData.lastFourDigits,
    expiryDate: financialData.expiryDate,
  };

  return (
    <>
      <OTPVerificationModal
        isOpen={showOTPModal}
        actionLabel="Submit"
        onSuccess={() => {
          setShowOTPModal(false);
          handleSubmitLimitRequest();
        }}
        onCancel={() => {
          setShowOTPModal(false);
          setShowLimitModal(true);
        }}
      />
      <section id="dashboard" className="py-16 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex justify-center mb-4 gap-3 flex-wrap">
              <button
                onClick={fetchUserData}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{config.labels.retry}</span>
              </button>
              <button
                onClick={() => handleManageCard()}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CreditCard className="h-4 w-4" />
                <span>{config.labels.manageCard}</span>
              </button>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 font-['Inter'] bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 bg-clip-text text-transparent">
              {config.labels.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
              {config.labels.subtitle}
            </p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <CreditCardDisplay cardData={cardData} />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50 dark:bg-gray-800/80 dark:border-gray-700/50"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{config.labels.creditUtilization}</h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">Used</span>
                    <span className="font-mono text-slate-900 dark:text-white">
                      Rs {financialData.consumedLimit.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-3 shadow-inner">
                    {/* <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${utilizationPercentage}%` }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 h-3 rounded-full shadow-sm"
                    /> */}
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${utilizationBarWidth}%` }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 h-3 rounded-full shadow-sm"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">
                      {utilizationPercentage}% utilized
                    </span>
                    <span className="font-mono text-slate-900 dark:text-white">
                      Rs {financialData.totalLimit.toLocaleString()} total
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <StatCard
                icon={Wallet}
                title={config.labels.availableLimit}
                value={`Rs ${financialData.availableLimit.toLocaleString()}`}
                subtitle="Ready to spend"
                color="green"
                delay={0.1}
              />
              <StatCard
                icon={TrendingUp}
                title="Limit Utilized"
                value={`Rs ${financialData.consumedLimit.toLocaleString()}`}
                subtitle="Current spending"
                color="amber"
                delay={0.2}
              />
              <StatCard
                icon={Gift}
                title={config.labels.rewardPoints}
                value={Number(financialData.points).toLocaleString()}
                subtitle="Available points"
                color="yellow"
                delay={0.3}
              />
              <StatCard
                customIcon="₨"
                title={config.labels.pointsValue}
                value={`Rs ${financialData.pointsValue.toLocaleString(undefined)}`}
                subtitle="Monetary equivalent"
                color="green"
                delay={0.4}
              />
            </div>
          </div>
          {cardList.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-12"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50 dark:bg-gray-800/80 dark:border-gray-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    {config.labels.cardList}
                  </h3>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {cardList.length} card{cardList.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.cardholderName}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.creditCardNumber}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.status}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.cardType}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.cardLimit}
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                          {config.labels.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cardList.map((card) => {
                        const statusBadge = getCardStatusBadge(card.status);
                        const typeBadge = getCardTypeBadge(card.cardType, card.isPrimary);
                        const StatusIcon = statusBadge.icon;

                        return (
                          <tr
                            key={card.id}
                            className={`border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700/50 cursor-pointer ${selectedCard?.id === card.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                            onClick={() => handleCardSelect(card)}
                          >
                            <td className="py-3 px-4">
                              <div className="font-medium text-slate-900 dark:text-white">
                                {card.cardholderName}
                              </div>
                            </td>
                            <td className="py-3 px-4 font-mono text-slate-700 dark:text-slate-300">
                              •••• •••• •••• {card.cardNumber.slice(-4)}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.classes}`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusBadge.label}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeBadge.color}-100 dark:bg-${typeBadge.color}-900/50 text-${typeBadge.color}-800 dark:text-${typeBadge.color}-300`}>
                                {typeBadge.label}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeBadge.color}-100 dark:bg-${typeBadge.color}-900/50 text-${typeBadge.color}-800 dark:text-${typeBadge.color}-300`}>
                                {card.isPrimary ? financialData.totalLimit.toLocaleString() : Number(card.suppCardLmt).toLocaleString()}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleManageCard(card);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  <Eye className="h-3 w-3" />
                                  {config.labels.manage}
                                </button>
                                {!card.isPrimary && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRequestLimitIncrease(card);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-200 shadow-sm hover:shadow-md"
                                  >
                                    <Plus className="h-3 w-3" />
                                    {config.labels.requestLimitIncrease}
                                  </button>
                                )}
                                {/* {isCardClosed(card.status) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadLetter(card);
                                    }}
                                    disabled={downloadingLetter}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-600 hover:text-white hover:border-slate-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Download className="h-3 w-3" />
                                    {downloadingLetter ? 'Downloading...' : 'Download Letter'}
                                  </button>
                                )} */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Limit Increase Modal */}
      {showLimitModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowLimitModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-slate-200/50 dark:border-gray-700/50 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              {config.labels.requestLimitIncrease}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              For card: <span className="font-mono">•••• •••• •••• {selectedCardForLimit?.cardNumber.slice(-4)}</span>
            </p>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {config.labels.monthlyExceedAmount}
              </label>
              {/* <input
                type="number"
                min="0"
                step="1000"
                value={exceedAmount}
                onChange={(e) => setExceedAmount(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder={`Enter amount in ${config.currency}`}
              /> */}
              <input
                type="number"
                min="0"
                step="1000"
                // This line checks if the value is 0 and returns an empty string to show the placeholder
                value={exceedAmount === 0 ? "" : exceedAmount}
                onChange={(e) => setExceedAmount(e.target.value === "" ? 0 : parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder={`Enter amount in ${config.currency}`}
              />
            </div>


            {/* Terms and Conditions */}
            <div className="flex items-start space-x-3 mt-4">
              <input
                id="terms"
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="terms" className="text-sm text-slate-600 dark:text-slate-400 leading-tight">
                I agree to the <a href="/TC_Supp_CC_SeparateLimit_9_December_2025.pdf" download="TCSuppCCSeparateLimitDecember2025.pdf" className="text-green-600 hover:underline font-medium">Terms and Conditions</a> regarding limit supplementary credit card.
              </label>
            </div>
            {/*  End */}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLimitModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                {config.labels.cancel}
              </button>
              <button
                onClick={() => {
                  if (!exceedAmount || exceedAmount <= 0) {
                    toast.error('Please enter a valid amount greater than 0.');
                    return;
                  }
                  if (!agreedToTerms) {
                    toast.error('Please agree to the terms and conditions.');
                    return;
                  }
                  setShowLimitModal(false);
                  setShowOTPModal(true);
                }}
                disabled={!agreedToTerms || !exceedAmount || exceedAmount <= 0}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${agreedToTerms && exceedAmount > 0
                    ? "bg-green-600 text-white hover:bg-green-700 shadow-md active:scale-95"
                    : "bg-slate-300 dark:bg-gray-700 text-slate-500 dark:text-slate-400 cursor-not-allowed opacity-70"
                  }`}
              >
                <span>{config.labels.submit}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Dashboard;